import React, { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import MobileStepItem from './MobileStepItem';
import { FiShield, FiMapPin, FiCalendar, FiUsers, FiTag, FiMail, FiPhone, FiX, FiArrowLeft, FiEdit2 } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import './MobileFlow.css';

const FULLSCREEN_STEPS = ['origin', 'destination', 'dates', 'passengers', 'email'];

const MobileOverlay = ({
  isOpen,
  onClose,
  activeStep,
  onStepChange,
  onSearch,
  // Insurance
  selectedInsurance,
  insuranceSummary,
  renderInsurance,
  // Origin
  showOrigin,
  originSummary,
  renderOrigin,
  // Destination
  showDestination,
  destinationSummary,
  renderDestination,
  // Product
  showProduct,
  productSummary,
  renderProduct,
  // Dates
  showDates,
  dateSummary,
  renderDates,
  // Passengers
  passengersSummary,
  renderPassengers,
  // Contact
  showEmail,
  showPhone,
  email,
  onEmailChange,
  emailError,
  phone,
  onPhoneChange,
  phoneError,
}) => {
  // Prevent body scroll when overlay is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Evitar scroll de la página cuando aparece el teclado virtual en pasos con keyboard
  const isKeyboardStepActive = FULLSCREEN_STEPS.includes(activeStep) && ['origin', 'destination'].includes(activeStep);
  useEffect(() => {
    if (!isKeyboardStepActive) return;
    const vv = window.visualViewport;
    if (!vv) return;
    const handleResize = () => {
      window.scrollTo({ top: 0, behavior: 'instant' });
    };
    vv.addEventListener('resize', handleResize);
    return () => vv.removeEventListener('resize', handleResize);
  }, [isKeyboardStepActive]);

  // Relay input ref: mantiene el teclado abierto durante transiciones entre keyboard steps
  const keyboardRelayRef = useRef(null);

  if (!isOpen) return null;

  // Build dynamic steps
  const steps = [];

  steps.push({
    id: 'insurance',
    label: 'Tipo de Seguro',
    icon: <FiShield />,
    summary: insuranceSummary,
    isCompleted: !!selectedInsurance,
    render: renderInsurance,
  });

  if (showOrigin) {
    steps.push({
      id: 'origin',
      label: 'Origen',
      icon: <FiMapPin />,
      summary: originSummary,
      isCompleted: !!originSummary,
      render: renderOrigin,
    });
  }

  if (showDestination) {
    steps.push({
      id: 'destination',
      label: 'Destino',
      icon: <FiMapPin />,
      summary: destinationSummary,
      isCompleted: !!destinationSummary,
      render: renderDestination,
    });
  }

  if (showProduct) {
    steps.push({
      id: 'product',
      label: 'Producto',
      icon: <FiTag />,
      summary: productSummary,
      isCompleted: !!productSummary,
      render: renderProduct,
    });
  }

  if (showDates) {
    steps.push({
      id: 'dates',
      label: 'Fechas',
      icon: <FiCalendar />,
      summary: dateSummary,
      isCompleted: !!dateSummary,
      render: renderDates,
    });
  }

  steps.push({
    id: 'passengers',
    label: 'Viajeros',
    icon: <FiUsers />,
    summary: passengersSummary,
    isCompleted: !!passengersSummary,
    render: renderPassengers,
  });

  if (showEmail) {
    steps.push({
      id: 'email',
      label: 'Email',
      icon: <FiMail />,
      summary: email || '',
      isCompleted: !!email && !emailError,
      render: () => (
        <div className="sf-fullscreen-contact">
          <div className={`sf-mobile-email-field ${emailError ? 'sf-mobile-email-field--error' : ''}`}>
            <div className="sf-mobile-email-field__icon"><FiMail /></div>
            <div className="sf-mobile-email-field__content">
              <label className="sf-mobile-email-field__label">EMAIL *</label>
              <input
                type="email"
                className="sf-mobile-email-field__input"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
              />
            </div>
          </div>
          {showPhone && (
            <div className="sf-mobile-phone-field">
              <div className="sf-mobile-phone-field__icon"><FiPhone /></div>
              <div className="sf-mobile-phone-field__content">
                <label className="sf-mobile-phone-field__label">TELÉFONO</label>
                <input
                  type="tel"
                  className={`sf-mobile-phone-field__input ${phoneError ? 'sf-mobile-phone-field__input--error' : ''}`}
                  placeholder="+1 234 567 890"
                  value={phone}
                  onChange={(e) => onPhoneChange(e.target.value)}
                />
              </div>
            </div>
          )}
          <p className="sf-mobile-consent__text">
            Al cotizar aceptas recibir comunicaciones de Segurosfly.com de acuerdo con nuestra{' '}
            <a
              href="https://segurosfly.com/politica-privacidad"
              target="_blank"
              rel="noopener noreferrer"
              className="sf-mobile-consent__link"
            >
              Política de Privacidad
            </a>.
          </p>
        </div>
      ),
    });
  }

  // Fullscreen mode detection
  const isFullscreen = FULLSCREEN_STEPS.includes(activeStep);
  const fullscreenStepData = isFullscreen ? steps.find(s => s.id === activeStep) : null;
  const KEYBOARD_STEPS = ['origin', 'destination'];
  const isKeyboardStep = KEYBOARD_STEPS.includes(activeStep);
  // TOP_STEPS: pasos con botón arriba (keyboard steps + passengers + email)
  const TOP_STEPS = ['origin', 'destination', 'passengers', 'email'];
  const isTopStep = TOP_STEPS.includes(activeStep);

  // Next step calculation
  const currentIndex = steps.findIndex(s => s.id === activeStep);
  const nextStep = currentIndex >= 0 && currentIndex < steps.length - 1
    ? steps[currentIndex + 1]
    : null;

  const handleNext = () => {
    // Email step: Cotizar en lugar de Siguiente
    if (activeStep === 'email') {
      onSearch();
      return;
    }
    if (nextStep) {
      const isCurrentlyKeyboard = KEYBOARD_STEPS.includes(activeStep);
      const willBeKeyboard = KEYBOARD_STEPS.includes(nextStep.id);

      if (isCurrentlyKeyboard && willBeKeyboard) {
        // Patrón relay: mover foco al input relay ANTES de desmontar el step actual.
        // Así el teclado nunca se cierra (siempre hay un input enfocado).
        // Luego flushSync desmonta origin y monta destination, y movemos foco al nuevo input.
        if (keyboardRelayRef.current) keyboardRelayRef.current.focus();
        flushSync(() => onStepChange(nextStep.id));
        const input = document.querySelector('.sf-mobile-sheet__fullscreen-content .react-select__input input');
        if (input) input.focus();
      } else {
        // Para transiciones a pasos sin teclado (ej: dates, passengers), no necesitamos relay
        flushSync(() => onStepChange(nextStep.id));
      }
    } else {
      onStepChange(null);
    }
  };

  const handleBack = () => {
    onStepChange(null);
  };

  return (
    <div className="sf-mobile-overlay">
      <div className="sf-mobile-overlay__backdrop" onClick={onClose} />
      <div className={`sf-mobile-sheet ${
        isFullscreen
          ? 'sf-mobile-sheet--fullscreen'
          : ''
      }`}>

        {isFullscreen ? (
          <>
            {/* Input relay invisible: mantiene el teclado abierto durante transiciones keyboard→keyboard */}
            <input
              ref={keyboardRelayRef}
              aria-hidden="true"
              tabIndex={-1}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                width: '50px',
                height: '44px',
                opacity: 0,
                pointerEvents: 'none',
                fontSize: '16px',
                transform: 'translate(-50%, -50%)',
                zIndex: -1,
              }}
            />
            {/* Fullscreen Header */}
            <div className="sf-mobile-sheet__header--fullscreen">
              <button type="button" className="sf-mobile-sheet__back" onClick={handleBack}>
                <FiArrowLeft />
              </button>
              <span className="sf-mobile-sheet__title">
                {fullscreenStepData?.label || ''}
              </span>
              <button type="button" className="sf-mobile-sheet__close" onClick={onClose}>
                <FiX />
              </button>
            </div>

            {/* Summary bar: pasos anteriores completados (estilo Airbnb) */}
            {currentIndex > 0 && (
              <div className="sf-mobile-sheet__summary-bar">
                {steps.slice(0, currentIndex).filter(s => s.isCompleted).map(step => (
                  <button
                    key={step.id}
                    type="button"
                    className="sf-mobile-sheet__summary-row"
                    onClick={() => onStepChange(step.id)}
                  >
                    <span className="sf-mobile-sheet__summary-icon">{step.icon}</span>
                    <span className="sf-mobile-sheet__summary-label">{step.label}</span>
                    <span className="sf-mobile-sheet__summary-value">{step.summary}</span>
                    <FiEdit2 className="sf-mobile-sheet__summary-edit" />
                  </button>
                ))}
              </div>
            )}

            {/* Fullscreen Content */}
            <div className="sf-mobile-sheet__body sf-mobile-sheet__body--fullscreen">
              <div className={`sf-mobile-sheet__fullscreen-content ${isKeyboardStep ? 'sf-mobile-sheet__fullscreen-content--compact' : ''}`}>

                {isTopStep ? (
                  <div className="sf-mobile-sheet__fullscreen-row">
                    <div className="sf-mobile-sheet__fullscreen-row-left">
                      <div className="sf-mobile-sheet__fullscreen-icon--sm">
                        {fullscreenStepData?.icon}
                      </div>
                      <h3 className="sf-mobile-sheet__fullscreen-label--sm">
                        {activeStep === 'origin' && '¿De dónde sales?'}
                        {activeStep === 'destination' && '¿A dónde viajas?'}
                        {activeStep === 'passengers' && '¿Cuántos viajan?'}
                        {activeStep === 'email' && '¿Dónde enviamos la cotización?'}
                      </h3>
                    </div>
                    <button
                      type="button"
                      className="sf-mobile-sheet__next-sm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleNext}
                    >
                      {activeStep === 'email' ? 'Cotizar' : (nextStep ? 'Siguiente' : 'Listo')}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="sf-mobile-sheet__fullscreen-icon">
                      {fullscreenStepData?.icon}
                    </div>
                    <h3 className="sf-mobile-sheet__fullscreen-label">
                      {activeStep === 'dates' && '¿Cuándo viajas?'}
                    </h3>
                  </>
                )}

                {fullscreenStepData?.render()}
              </div>
            </div>

            {/* Footer solo para dates (no keyboard steps) */}
            {activeStep === 'dates' && (
              <div className="sf-mobile-sheet__footer">
                <button
                  type="button"
                  className="sf-mobile-sheet__cta"
                  onClick={handleNext}
                >
                  <span>{nextStep ? 'Siguiente' : 'Listo'}</span>
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Accordion Header */}
            <div className="sf-mobile-sheet__header">
              <div className="sf-mobile-sheet__drag-handle" />
              <div className="sf-mobile-sheet__title-row">
                <span className="sf-mobile-sheet__title">Buscar seguro de viaje</span>
                <button type="button" className="sf-mobile-sheet__close" onClick={onClose}>
                  <FiX />
                </button>
              </div>
            </div>

            {/* Accordion Steps */}
            <div className="sf-mobile-sheet__body">
              {steps.map((step) => (
                <MobileStepItem
                  key={step.id}
                  label={step.label}
                  summary={step.summary}
                  isActive={activeStep === step.id}
                  isCompleted={step.isCompleted}
                  onToggle={() => onStepChange(activeStep === step.id ? null : step.id)}
                  icon={step.icon}
                >
                  {step.render()}
                </MobileStepItem>
              ))}

            </div>

            {/* Footer CTA */}
            <div className="sf-mobile-sheet__footer">
              <button
                type="button"
                className="sf-mobile-sheet__cta"
                onClick={onSearch}
              >
                <FaCalculator />
                <span>Cotizar</span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MobileOverlay;
